import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "./GrafikDashboard.css";

import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import ChartDataLabels from 'chartjs-plugin-datalabels';

ChartJS.register(ChartDataLabels);
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

export const GrafikDashboard = () => {
  const navigate = useNavigate();
  const [apiData, setApiData] = useState({
    overall: null,
    kecamatan: [],
    backlog: []
  });
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });
  
  const chartContainerRef1 = useRef(null);
  const chartContainerRef2 = useRef(null);

  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_URL}/dataall`, { withCredentials: true });

        if (response.data && response.data.code === 200) {
          const data = response.data.dataTotal;
          
          // Pisahkan data berdasarkan statType
          const overallData = data.find(item => item.statType === "overall");
          const kecamatanData = data.filter(item => item.statType === "kecamatan");
          const backlogData = data.filter(item => item.statType === "backlog");
          
          setApiData({
            overall: overallData,
            kecamatan: kecamatanData,
            backlog: backlogData
          });
        } else {
          console.error("Error fetching data:", response.statusText);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, [navigate]);

  const getFontSize = (baseSize) => {
    if (windowSize.width < 480) return baseSize * 0.6;
    if (windowSize.width < 768) return baseSize * 0.8;
    return baseSize;
  };

  const getKecamatanDatasets = () => {
    return [
      {
        label: "Rumah Layak Huni",
        data: apiData.kecamatan.map(item => item.layakHuni),
        backgroundColor: "rgba(75, 192, 192, 0.8)",
        borderColor: "rgba(75, 192, 192, 1)",
        borderWidth: 1,
      },
      {
        label: "Rumah Tidak Layak Huni",
        data: apiData.kecamatan.map(item => item.tidakLayakHuni),
        backgroundColor: "rgba(255, 99, 132, 0.8)",
        borderColor: "rgba(255, 99, 132, 1)",
        borderWidth: 1,
      },
      {
        label: "Berpenghuni",
        data: apiData.kecamatan.map(item => item.berpenghuni),
        backgroundColor: "rgba(54, 162, 235, 0.8)",
        borderColor: "rgba(54, 162, 235, 1)",
        borderWidth: 1,
      },
      {
        label: "Tidak Berpenghuni",
        data: apiData.kecamatan.map(item => item.tidakBerpenghuni),
        backgroundColor: "rgba(255, 206, 86, 0.8)",
        borderColor: "rgba(255, 206, 86, 1)",
        borderWidth: 1,
      },
      {
        label: "Backlog",
        data: apiData.kecamatan.map(item => item.backlog),
        backgroundColor: "rgba(153, 102, 255, 0.8)",
        borderColor: "rgba(153, 102, 255, 1)",
        borderWidth: 1,
      }
    ];
  };

  const kecamatanChartData = {
    labels: apiData.kecamatan.map(item => item.category),
    datasets: getKecamatanDatasets()
  };

  const getTotalDatasets = () => {
    if (!apiData.overall) return [];
    
    return [
      {
        label: "Rumah Layak Huni",
        data: [apiData.overall.layakHuni],
        backgroundColor: "rgba(75, 192, 192, 0.8)",
        borderColor: "rgba(75, 192, 192, 1)",
        borderWidth: 1,
      },
      {
        label: "Rumah Tidak Layak Huni",
        data: [apiData.overall.tidakLayakHuni],
        backgroundColor: "rgba(255, 99, 132, 0.8)",
        borderColor: "rgba(255, 99, 132, 1)",
        borderWidth: 1,
      },
      {
        label: "Berpenghuni",
        data: [apiData.overall.berpenghuni],
        backgroundColor: "rgba(54, 162, 235, 0.8)",
        borderColor: "rgba(54, 162, 235, 1)",
        borderWidth: 1,
      },
      {
        label: "Tidak Berpenghuni",
        data: [apiData.overall.tidakBerpenghuni],
        backgroundColor: "rgba(255, 206, 86, 0.8)",
        borderColor: "rgba(255, 206, 86, 1)",
        borderWidth: 1,
      },
      {
        label: "Backlog",
        data: [apiData.overall.backlog],
        backgroundColor: "rgba(153, 102, 255, 0.8)",
        borderColor: "rgba(153, 102, 255, 1)",
        borderWidth: 1,
      }
    ];
  };

  const totalChartData = {
    labels: ["Total Keseluruhan"],
    datasets: getTotalDatasets()
  };

  const getChartOptions = (title, isTotalChart = false) => {
    const baseFontSize = getFontSize(14);
    const titleFontSize = getFontSize(16);
    const labelFontSize = getFontSize(12);
    
    const isSmallScreen = windowSize.width < 768;
    
    return {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: isSmallScreen ? "bottom" : "top",
          labels: {
            boxWidth: isSmallScreen ? 10 : 20,
            padding: isSmallScreen ? 5 : 10,
            font: { size: baseFontSize }
          }
        },
        title: {
          display: true,
          text: title,
          font: { size: titleFontSize, weight: 'bold' },
          padding: { top: 10, bottom: 15 }
        },
        datalabels: {
          display: isTotalChart || !isSmallScreen,
          anchor: 'end',
          align: 'top',
          font: { size: labelFontSize },
          color: '#444',
          formatter: (value) => value > 0 ? value : '',
        },
        tooltip: {
          enabled: true,
          mode: 'index',
          intersect: false,
          padding: 10,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          titleFont: { size: baseFontSize },
          bodyFont: { size: baseFontSize },
          displayColors: true,
          callbacks: {
            title: (tooltipItems) => {
              return isTotalChart ? "Total Statistik" : `Kecamatan ${tooltipItems[0].label}`;
            }
          }
        }
      },
      scales: {
        x: {
          ticks: { 
            font: { size: labelFontSize },
            maxRotation: isSmallScreen ? 0 : 45,
            minRotation: isSmallScreen ? 0 : 45,
          },
          grid: {
            display: false
          }
        },
        y: {
          ticks: { 
            font: { size: labelFontSize },
            callback: function(value) {
              if (isSmallScreen && value >= 1000) {
                return (value / 1000).toFixed(1) + 'K';
              }
              return value;
            }
          },
          beginAtZero: true,
          grid: {
            color: 'rgba(0, 0, 0, 0.1)'
          }
        }
      },
      layout: {
        padding: {
          top: isSmallScreen ? 10 : 20,
          bottom: isSmallScreen ? 15 : 0
        }
      },
      animation: {
        duration: isSmallScreen ? 500 : 1000
      }
    };
  };

  return (
    <div className="dashboard2">
      {/* Statistik Cards */}
      {apiData.overall && (
        <div className="stats-cards-container">
          <div className="stat-card">
            <h3>Total Rumah</h3>
            <p>{apiData.overall.berpenghuni + apiData.overall.tidakBerpenghuni}</p>
          </div>
          <div className="stat-card">
            <h3>Rumah Layak Huni</h3>
            <p>{apiData.overall.layakHuni}</p>
          </div>
          <div className="stat-card">
            <h3>Rumah Tidak Layak Huni</h3>
            <p>{apiData.overall.tidakLayakHuni}</p>
          </div>
          <div className="stat-card">
            <h3>Berpenghuni</h3>
            <p>{apiData.overall.berpenghuni}</p>
          </div>
          <div className="stat-card">
            <h3>Tidak Berpenghuni</h3>
            <p>{apiData.overall.tidakBerpenghuni}</p>
          </div>
          <div className="stat-card">
            <h3>Backlog</h3>
            <p>{apiData.overall.backlog}</p>
          </div>
        </div>
      )}
      
      {/* Chart 1: Per Kecamatan Stats */}
      {apiData.kecamatan.length > 0 && (
        <div 
          className="chart-container" 
          ref={chartContainerRef1}
          style={{ display: 'block' }}
        >
          <Bar 
            data={kecamatanChartData} 
            options={getChartOptions('Statistik Perumahan Per Kecamatan')} 
            height={windowSize.width < 768 ? 500 : 350}
          />
        </div>
      )}
      
      {/* Chart 2: Total Stats */}
      {apiData.overall && (
        <div 
          className="chart-container" 
          ref={chartContainerRef2}
          style={{ display: 'block' }}
        >
          <Bar 
            data={totalChartData} 
            options={getChartOptions('Statistik Total Perumahan', true)} 
            height={windowSize.width < 768 ? 450 : 350}
          />
        </div>
      )}
    </div>
  );
};